import { Link } from "react-router-dom";
import BinderBuildersDisclaimer from "../components/BinderBuildersDisclaimer";
import BinderBuildersInquiryForm, {
  BINDER_BUILDERS_EMAIL,
} from "../components/BinderBuildersInquiryForm";

const productOptions = [
  {
    id: "builder-packs",
    title: "Builder Packs",
    positioning: "10-card Pokemon packs",
    description:
      "Affordable Pokemon card packs for loot bags, rewards, small gifts, and new collectors.",
    highlights: [
      "10 Pokemon cards per pack",
      "No duplicates inside a single pack",
      "No Energy or Trainer cards",
    ],
  },
  {
    id: "builder-bundles",
    title: "Builder Bundles",
    positioning: "50-card Pokemon bundles",
    description:
      "A simple way to grow a binder or kickstart a collection with sorted Pokemon cards.",
    highlights: [
      "50 Pokemon cards",
      "No duplicates inside a single bundle",
      "No Energy or Trainer cards",
    ],
  },
  {
    id: "builder-decks",
    title: "Builder Decks",
    positioning: "60-card casual beginner decks",
    description:
      "Ready-to-play casual decks for kids learning Pokemon TCG turns, Energy, attacks, and Trainer cards.",
    highlights: [
      "Includes Pokemon, Trainer, and Energy cards",
      "Built to help kids learn the game",
      "Not tournament-focused",
    ],
  },
];

export default function BinderBuilders() {
  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <header style={styles.hero}>
          <div style={styles.logoFrame}>
            <img
              src="/images/new-binder-builders-logo.png"
              alt="B's Binder Builders"
              style={styles.logo}
            />
          </div>
          <div style={styles.heroCopy}>
            <div style={styles.eyebrow}>Collect • Build • Play</div>
            <h1 style={styles.h1}>Big fun for growing collections.</h1>
            <p style={styles.tagline}>
              Honest, beginner-friendly Pokemon card packs and casual decks
              for young collectors.
            </p>
            <a href="#products" style={styles.heroCta}>
              Explore the lineup
            </a>
          </div>
        </header>

        <div style={styles.divider} aria-hidden="true" />

        <section style={styles.about}>
          <div style={styles.aboutLabel}>Made for new collectors</div>
          <p style={styles.aboutCopy}>
            B's Binder Builders helps young Pokemon fans build their binders,
            grow their collections, and learn the game through honest,
            beginner-friendly Pokemon card packs and casual decks.
          </p>
          <p style={styles.aboutCopy}>
            We are a small family-run project focused on transparent,
            kid-friendly packs for sorting, trading, collecting, gifting,
            party favours, rewards, and casual learning.
          </p>
          <p style={styles.aboutCopyLast}>
            Questions or ready to order?{" "}
            <a href="#order-inquiry" style={styles.inlineLink}>
              Contact us
            </a>{" "}
            or email{" "}
            <a href={`mailto:${BINDER_BUILDERS_EMAIL}`} style={styles.inlineLink}>
              {BINDER_BUILDERS_EMAIL}
            </a>
            .
          </p>
        </section>

        <div style={styles.divider} aria-hidden="true" />

        <main id="products" style={styles.productsSection}>
          <div style={styles.sectionHeadingRow}>
            <div>
              <div style={styles.sectionKicker}>Choose your next pull</div>
              <h2 style={styles.sectionTitle}>What we make</h2>
            </div>
          </div>
          <div style={styles.grid}>
            {productOptions.map((opt, index) => (
              <article key={opt.id} style={styles.card}>
                <div style={styles.cardTopLine} aria-hidden="true" />
                <div>
                  <div style={styles.cardMetaRow}>
                    <div style={styles.cardPositioning}>{opt.positioning}</div>
                    <div style={styles.cardNumber}>0{index + 1}</div>
                  </div>
                  <div style={styles.cardTitle}>{opt.title}</div>
                  <p style={styles.cardDesc}>{opt.description}</p>
                  <ul style={styles.bullets}>
                    {opt.highlights.map((h, i) => (
                      <li key={i} style={styles.bulletItem}>{h}</li>
                    ))}
                  </ul>
                </div>
                <div style={styles.cardFooter}>
                  <Link to={`/binder-builders/${opt.id}`} style={styles.cta}>
                    View details
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </main>

        <BinderBuildersInquiryForm />

        <BinderBuildersDisclaimer style={styles.disclaimer} />
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    overflowX: "hidden",
    background: "#1F2528",
    color: "#F7F3E8",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    padding: "clamp(16px, 4vw, 40px) 16px",
  },

  shell: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 1100,
    margin: "0 auto",
    padding: "clamp(8px, 2vw, 24px)",
  },

  hero: {
    boxSizing: "border-box",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    alignItems: "center",
    gap: "clamp(24px, 5vw, 56px)",
    minHeight: 380,
    padding: "clamp(24px, 5vw, 48px)",
    border: "2px solid #6897A9",
    borderRadius: 20,
    background: "#283034",
    boxShadow: "0 14px 32px rgba(0, 0, 0, 0.24)",
  },

  logoFrame: {
    boxSizing: "border-box",
    justifySelf: "center",
    width: "100%",
    maxWidth: 430,
    minWidth: 0,
    aspectRatio: "16 / 9",
    padding: 12,
    overflow: "hidden",
    border: "2px solid #F0DBA5",
    borderRadius: 16,
    background: "#F7F3E8",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
  },

  logo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    borderRadius: 10,
  },

  heroCopy: {
    position: "relative",
    zIndex: 1,
    minWidth: 0,
  },

  eyebrow: {
    display: "inline-block",
    marginBottom: 14,
    padding: "6px 12px",
    border: "1px solid #F0DBA5",
    borderRadius: 999,
    color: "#F0DBA5",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
  },

  h1: {
    margin: 0,
    fontSize: "clamp(36px, 6vw, 66px)",
    fontWeight: 900,
    letterSpacing: "-0.035em",
    lineHeight: 1.02,
    color: "#F7F3E8",
  },

  tagline: {
    margin: "18px 0 24px 0",
    maxWidth: 540,
    fontSize: 18,
    lineHeight: 1.6,
    color: "#D8DEE2",
  },

  heroCta: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 18px",
    border: "1px solid #F0DBA5",
    borderRadius: 10,
    background: "#6897A9",
    color: "#1F2528",
    fontSize: 15,
    fontWeight: 900,
    textDecoration: "none",
  },

  divider: {
    height: 2,
    margin: "28px 0",
    borderRadius: 2,
    background: "#6897A9",
    opacity: 0.7,
  },

  about: {
    boxSizing: "border-box",
    width: "100%",
    background: "#F7F3E8",
    border: "2px solid #6897A9",
    borderLeft: "6px solid #F0DBA5",
    borderRadius: 14,
    padding: "clamp(20px, 4vw, 28px)",
    marginBottom: 4,
  },

  aboutLabel: {
    marginBottom: 12,
    color: "#636466",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },

  aboutCopy: {
    margin: "0 0 12px 0",
    fontSize: 15,
    lineHeight: 1.65,
    maxWidth: 820,
    color: "#1F2528",
  },

  aboutCopyLast: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.65,
    maxWidth: 820,
    color: "#1F2528",
    overflowWrap: "anywhere",
  },

  inlineLink: {
    color: "#385D6B",
    fontWeight: 800,
    textDecorationColor: "#6897A9",
    textUnderlineOffset: 3,
  },

  productsSection: {
    scrollMarginTop: 24,
  },

  sectionHeadingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
    marginBottom: 18,
  },

  sectionKicker: {
    marginBottom: 5,
    color: "#F0DBA5",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
  },

  sectionTitle: {
    margin: 0,
    fontSize: "clamp(26px, 4vw, 38px)",
    fontWeight: 900,
    letterSpacing: "-0.025em",
    color: "#F7F3E8",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
  },

  card: {
    boxSizing: "border-box",
    minWidth: 0,
    position: "relative",
    overflow: "hidden",
    border: "2px solid #6897A9",
    borderRadius: 16,
    padding: "22px 20px 20px",
    background: "#F7F3E8",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 350,
    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.18)",
  },

  cardTopLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: "#6897A9",
  },

  cardMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  cardPositioning: {
    display: "inline-block",
    background: "#DCE7EA",
    border: "1px solid #6897A9",
    color: "#385D6B",
    fontWeight: 900,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "5px 9px",
    borderRadius: 999,
  },

  cardNumber: {
    color: "#636466",
    fontSize: 15,
    fontWeight: 900,
  },

  cardTitle: {
    fontWeight: 900,
    fontSize: 26,
    margin: "18px 0 8px",
    color: "#1F2528",
    letterSpacing: "-0.02em",
  },

  cardDesc: {
    margin: "0 0 12px 0",
    fontSize: 14,
    lineHeight: 1.55,
    color: "#636466",
  },

  bullets: {
    margin: "0 0 16px 0",
    paddingLeft: 18,
  },

  bulletItem: {
    marginBottom: 6,
    fontSize: 14,
    lineHeight: 1.45,
    color: "#1F2528",
  },

  cardFooter: {
    marginTop: "auto",
  },

  cta: {
    display: "inline-flex",
    color: "#1F2528",
    background: "#F0DBA5",
    border: "1px solid #6897A9",
    padding: "10px 14px",
    borderRadius: 9,
    textDecoration: "none",
    fontWeight: 900,
    fontSize: 14,
  },

  disclaimer: {
    marginTop: 20,
  },

};
